import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import gql from 'graphql-tag';
import { FaSpinner } from 'react-icons/fa';
import { useMutation, useApolloClient } from 'react-apollo';
import Input from '../Input';
import useQueryString from '../../hooks/useQueryString';
import useToast from '../../hooks/useToast';
import useContext from '../../hooks/useContext';
import { phoneMask, makePhoneNumber } from '../../utils/masks';
import firebase, { updateCurrentUser } from '../../services/firebase';

const registerGql = gql`
  mutation register($user: RegisterUser!) {
    register(user: $user) {
      name
      condominiums {
        data
      }
    }
  }
`;
const checkUserGql = gql`
  query checkUser($user: RegisterUser!, $from: String) {
    checkUser(user: $user, from: $from) {
      email
      phoneNumber
    }
  }
`;

export default function UserRegister() {
  const history = useHistory();
  const toast = useToast();
  const { write } = useContext();
  const [submitting, setSubmitting] = useState(false);
  const apolloClient = useApolloClient(0);
  const { currentUser } = firebase.auth();

  const [registerFn] = useMutation(registerGql, {
    onCompleted: async ({ register }) => {
      write({
        condominiumId: register.condominiums[0].data,
        callback: () => {
          setSubmitting(false);
          toast.fire({
            icon: 'success',
            title: `Bem vindo(a) ${register.name}`,
          });
          history.push('/dashboard');
        },
      });
    },
    onError: () => {
      setSubmitting(false);
      toast.fire({
        icon: 'error',
        title: 'Problemas ao registrar. Tente novamente',
      });
    },
  });
  const {
    handleChange,
    validateForm,
    setFieldValue,
    values: { user },
    errors,
  } = useFormikContext();
  const querystring = useQueryString();

  const slug = querystring.get('slug');

  const next = async e => {
    e.preventDefault();
    setSubmitting(true);
    const _user = { ...user };
    _user.phoneNumber = makePhoneNumber({
      country: user.country,
      phone: user.phoneNumber,
    });
    _user.country = undefined;
    const validation = await validateForm();
    if (validation.user) {
      setSubmitting(false);
      return undefined;
    }

    const { data } = await apolloClient.query({
      query: checkUserGql,
      fetchPolicy: 'network-only',
      variables: {
        user: _user,
        from: currentUser.email ? 'email' : 'phoneNumber',
      },
    });

    if (data.checkUser.phoneNumber && data.checkUser.email) {
      setSubmitting(false);
      return toast.fire({
        icon: 'error',
        title: `O email/telefone já está cadastrado.`,
      });
    }

    if (data.checkUser.email) {
      setSubmitting(false);
      return toast.fire({
        icon: 'error',
        title: `Este email já está sendo utilizado.`,
      });
    }

    if (data.checkUser.phoneNumber) {
      setSubmitting(false);
      return toast.fire({
        icon: 'error',
        title: `Este número já está sendo utilizado.`,
      });
    }

    if (slug) {
      await updateCurrentUser(_user);
      return registerFn({ variables: { user: _user } });
    }

    if (validation.user) return undefined;
    return setFieldValue('step', 'condominium');
  };

  return (
    <div className="user-register d-flex flex-column justify-content-between">
      <form className="form " noValidate onSubmit={next}>
        <div className="justify-content-center d-flex p-3">
          <h4>Cadastre-se</h4>
        </div>
        <div>
          <div className="form-group">
            <label>Nome</label>
            <Input
              focus
              className={classnames('form-control', {
                'is-invalid': errors.user && errors.user.name,
              })}
              name="user.name"
              value={user.name}
              onChange={handleChange}
            />
          </div>
          <div className="form-group ">
            <label>E-mail</label>
            <Input
              className={classnames('form-control', {
                'is-invalid': errors.user && errors.user.email && !currentUser.email,
              })}
              type="email"
              name="user.email"
              value={user.email}
              disabled={currentUser && currentUser.email}
              onChange={handleChange}
            />
          </div>
          <div className="form-group text-left">
            <label>Telefone (com DDD)</label>
            <div className="input-group mb-3 row">
              <div className="col-4">
                <Input disabled value={user.country} name="user.country" onChange={handleChange} />
              </div>
              <Input
                mask={phoneMask}
                className={classnames('form-control', {
                  'is-invalid': errors.user && errors.user.phoneNumber,
                })}
                value={user.phoneNumber}
                name="user.phoneNumber"
                onChange={handleChange}
                disabled={currentUser.phoneNumber}
              />
            </div>
          </div>
          {slug && (
            <div className="form-group">
              <label>Unidade (Apartamento/bloco) </label>
              <Input
                placeholder="102 A"
                className={classnames('form-control', {
                  'is-invalid': errors.user && errors.user.unity,
                })}
                value={user.unity}
                onChange={handleChange}
                name="user.unity"
              />
            </div>
          )}
          <button className="btn btn-block btn-primary" type="submit" id="register-btn" disabled={submitting}>
            {slug ? 'Cadastrar' : 'Próximo'}
            {submitting && <FaSpinner className="spin" />}
          </button>
        </div>
      </form>
    </div>
  );
}
